/**
 * The Time Series Picker (/common/time-series-picker/time-series-picker.vue) returns a time series with the format:
 * {
 *     label - the full label for the date range. Formatted as "Dec 1, 2020 - Dec 7, 2020"
 *     config: {
 *         start: date-formatted start of range, inclusive - 2020-12-01
 *         end: date-formatted end of range, exclusive - 2020-12-08
 *         type: the chosen type of date range - either a default like "Last x Days" or "Custom"
 *     }
 *     spec: { - used as the timeSeries for aggregations, but requires conversion of "first" field
 *         first: date-formatted start of range - 2020-12-01
 *         count: number of days in range - 7
 *         period: the range of the date range - this will always be "dayRange"
 *     }
 * }
 *
 * Think of config.start/end as pointers to midnight on their respective days. The range covers all 24 hours of each day
 * in the range. The label, on the other hand, is the human-readable alternative where both endpoints are inclusive.
 *
 * Note that spec.first is converted to a numeric timestamp by VanillaDateRange. This happens
 * automatically for time series passed to the Vuex global filters. Since some users of time series objects do not have
 * knowledge of whether this conversion has happened, the utilities here should manually type-check to see if conversion
 * is required.
 *
 * See global-filters.module.js:setTimeSeries() for where Vuex calls AngularJS for the spec conversion.
 * aggregation-service.js:getTimeSeriesSpec() is where DateRange.js ends up being used.
 */

import get from 'lodash/get';
import isString from 'lodash/isString';
import {
    formatDates,
    createTimeSeriesFromDate,
    convertConfigToTimeSeries,
    getFirstLastDateValues,
} from '@/common/time-series-picker/time-series-picker.util.js';
import moment from '@utils/moment-wrapper';

export function isDefaultDateRange (dateRange) {
    return !!get(dateRange, 'spec') && !!get(dateRange, 'label') && !!get(dateRange, 'config');
}

export const isWholeNumber = /^-?\d+$/;

export const isISODateString = /^\d{4}-\d{2}-\d{2}$/;

const outerQuotes = /^['"]+|['"]+$/g;

export function sanitizeAggregationDate (raw) {
    // Trim any quotes from beginning/end, for uniform checking later on.
    if (isString(raw)) {
        raw = raw.replace(outerQuotes, '');
    }

    // Generic timestamps can be processed as-is by the backend.
    if (isWholeNumber.test(raw)) {
        return raw;
    }

    if (isISODateString.test(raw)) {
        return `date("${raw}")`;
    }

    if (raw.startsWith('date')) {
        return raw;
    }

    throw new Error(`Unrecognized aggregation date format "${raw}"`);
}

export function convertSpecForAggregation (spec) {
    return {
        ...spec,
        first: sanitizeAggregationDate(spec.first),
    };
}

// Very old dashboard widgets save the date range as
// { type: 'Last x Days' } or { type: custom, start: x, last: x }
// It is easier to convert them at the start
export function convertTypeToFullDateRange ({ type, start, end }) {
    const timeSeries = createTimeSeriesFromDate(formatDates({ type, start, end }));

    return {
        timeSeries,
        type,
    };
}

// This function is for saved date ranges that aren't updated day-to-day
// we can't use the saved spec because it was generated for a previous day
export function convertTimeSeriesForAggregation (
    { config, spec },
    preferNoDataTrustIssues = false,
    adjustForSessionReplay = false,
) {
    const { type } = config;

    if (type === 'Today') {
        return {
            ...spec,
            first: 'startOfPeriod("daily", now())',
        };
    }

    const { count, period } = spec;

    if (/Last (\d*) Day(s) \(and today\)/.test(type)) {
        return {
            ...spec,
            count,
            first: `dateAdd(startOfPeriod("daily", now()), ${(count - 1) * -1}, "days")`,
        };
    }
    if (type === 'Yesterday' || (!preferNoDataTrustIssues && count !== undefined && /Last (\d)* Day(s)?/.test(type))) {
        return {
            ...spec,
            count: adjustForSessionReplay ? count + 1 : count,
            first: `dateAdd(startOfPeriod("daily", now()), ${count * -1}, "days")`,
        };
    }
    if (preferNoDataTrustIssues && type && /Last (\d)* Day(s)?/.test(type)) {
        const firstAndLast = getFirstLastDateValues(type, period);
        if (firstAndLast) {
            return firstAndLast;
        }
    }
    if (preferNoDataTrustIssues && type === 'Custom' && count > 1) {
        const timeInterval = period === 'hourRange' || period === 'hourly' ? 'hours' : 'days';

        return {
            first: `date("${config.start}")`,
            last: `dateAdd(date("${config.end}"), -1, "${timeInterval}")`,
            period,
        };
    }

    return convertSpecForAggregation(spec);
}

export function convertConfigForAggregation (config) {
    const timeSeries = convertConfigToTimeSeries(config);

    return convertTimeSeriesForAggregation(timeSeries);
}

export function convertTimeSeriesToRange ({ config, spec }) {
    const { start, end } = config;
    const { count } = spec;

    return {
        start,
        end,
        count,
    };
}

export function convertSpecForIterativeAggregation (spec) {
    let iterativePeriod;
    switch (spec.period) {
        case 'hourRange':
            iterativePeriod = 'hourly';
            break;
        case 'dayRange':
            iterativePeriod = 'daily';
            break;
        case 'weekRange':
            iterativePeriod = 'weekly';
            break;
        case 'monthRange':
            iterativePeriod = 'monthly';
            break;
    }

    return {
        ...spec,
        first: `date("${spec.first}")`,
        period: iterativePeriod,
    };
}

export function getAppropriateLabel (dateRange) {
    if (dateRange.config.type === 'Custom') {
        return dateRange.label;
    }

    return dateRange.config.type;
}

export function validPeriodsForCount (count) {
    if (count < 2) {
        return ['hourly'];
    }

    if (count === 2) {
        return ['hourly', 'daily'];
    }

    if (count > 2 && count < 14) {
        return ['daily'];
    }

    if (count >= 14 && count < 56) {
        return ['daily', 'weekly'];
    }

    if (count >= 56 && count < 90) {
        return ['daily', 'weekly', 'monthly'];
    }

    return ['weekly', 'monthly'];
}

export function getValidPeriodsForDateRange (dateRange) {
    const count = get(dateRange, 'spec.count');

    return validPeriodsForCount(count);
}

export function getTimeSeriesByPeriod (period, timeSeries) {
    const { config, spec } = timeSeries;
    let { start, end } = config;
    let { count, first } = spec;

    // When a new user is created in Pendo, their default date range is 'Last 30 days' and the start and end fields are missing from the config object. These will act as defaults.
    start = start || moment(first).format('YYYY-MM-DD');
    end =
        end ||
        moment(first)
            .add(count, 'days')
            .format('YYYY-MM-DD');

    if (period === 'hourly') {
        if (count === 0) count = 24;
        else count *= 24;
    } else if (period === 'weekly') {
        count = Math.ceil(count / 7);
        if (moment(start).day() - moment(end).day() > 0) {
            count++;
        }
    } else if (period === 'monthly') {
        count = getMonthlyCount(config);
    }

    return {
        period,
        first: `startOfPeriod("${period}", date("${start}"))`,
        count,
    };
}

export function getYears (start, end) {
    const startYear = moment(start).year();
    const endYear = moment(end).year();

    return { startYear, endYear };
}

export function getMonthlyCount ({ start, end }) {
    const { startYear, endYear } = getYears(start, end);
    const MONTHS_IN_YEAR = 12;
    const startMonth = moment(start).month();
    const endMonth = moment(end).month();

    if (endYear === startYear) {
        return endMonth - startMonth + 1;
    }

    let months = MONTHS_IN_YEAR - startMonth + (endMonth + 1);
    months += MONTHS_IN_YEAR * (endYear - startYear - 1);

    return months;
}

export function getTimeSeriesShortcuts (createdAt, options = {}) {
    const {
        showSinceCreation = false,
        showToday = true,
        showYesterday = true,
        showLast7Days = true,
        showLast30Days = true,
        showLast90Days = true,
        ignoreTimeZone = true,
        entityType,
    } = options;

    const defaultValueFromComponents = [
        showToday && { label: 'Today', value: { start: 0, end: 0 } },
        showYesterday && { label: 'Yesterday', value: { start: -1, end: -1 } },
        showLast7Days && { label: 'Last 7 Days', value: { start: -1, end: -7 } },
        showLast30Days && { label: 'Last 30 Days', value: { start: -1, end: -30 } },
        showLast90Days && { label: 'Last 90 Days', value: { start: -1, end: -90 } },
    ].filter(Boolean);

    const list = [];

    if (showSinceCreation) {
        const start = moment(createdAt, { ignoreTimeZone }).startOf('day');
        const end = moment({ ignoreTimeZone }).startOf('day');

        list.push({
            label: `Since ${entityType} Creation`,
            type: 'Custom',
            value: {
                start: 0,
                end: -end.diff(start, 'days'),
            },
        });
    }

    list.push(...defaultValueFromComponents);

    return list;
}
